import React, { useEffect, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { schuljahrKurz } from '../../lib/schuljahr';
import { apiFetch, apiPost, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { notenFarbeKMK } from '../../lib/utils';
import { ART_LABEL } from '../../lib/constants';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdSheetHeader, LdField, LdSelectField, LdChipGroup, LdChip, LdGradePicker, LdSegmented, LdTextarea, LdSheetSaveBar, LdInputField } from '../../components/ld/LdForm';
import { DbNote } from '../../types';
import { zaehltText } from '../../lib/noten-zaehlt';
import '../schueler/U6Bearbeiten.css';
import { LdDatum } from '../../components/ld/LdFenster';

type Art = 'KA' | 'Test' | 'Muendlich' | 'Sonstiges';
const ARTEN: Art[] = ['KA', 'Test', 'Muendlich', 'Sonstiges'];
const ttmm = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

// Welle U6a: EIN Noten-Sheet für Anlegen und Bearbeiten (Profil + Notenübersicht).
// note = null → neue Note (POST); sonst PUT + Löschen (Bestätigung erst nach Schließen des
// Sheets, weil ein modales Sheet den Dialog sonst blockiert).
export function NoteSheet({ open, note, schueler, faecher, vorbelegtFach, halbjahr, schuljahr, onClose, onWiederOeffnen, onGespeichert, onGeloescht }: {
  open: boolean;
  note: DbNote | null;
  schueler: { id: number; name: string; klasse_id: number | null };
  faecher: string[];
  vorbelegtFach?: string;
  halbjahr: 'HJ1' | 'HJ2';
  schuljahr: string;
  onClose: () => void;
  onWiederOeffnen?: () => void;
  onGespeichert: (note: DbNote, neu: boolean) => void;
  onGeloescht?: (id: number) => void;
}) {
  const neu = note === null;
  const [fach, setFach] = useState('');
  const [art, setArt] = useState<Art>('KA');
  const [wert, setWert] = useState(3);
  const [gewicht, setGewicht] = useState(1);
  const [datum, setDatum] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [kommentar, setKommentar] = useState('');
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    if (!open) return;
    setFehler('');
    if (note) {
      setFach(note.fach); setArt(note.art as Art); setWert(note.note); setGewicht(note.gewichtung ?? 1);
      setDatum(note.datum.slice(0, 10)); setKommentar(note.kommentar ?? '');
    } else {
      setFach(vorbelegtFach ?? faecher[0] ?? ''); setArt('KA'); setWert(3); setGewicht(1);
      setDatum(new Date().toLocaleDateString('sv-SE')); setKommentar('');
    }
  }, [open, note, vorbelegtFach]); // eslint-disable-line react-hooks/exhaustive-deps

  async function speichern() {
    if (!fach.trim()) return;
    setLaden(true); setFehler('');
    try {
      if (neu) {
        const n = await apiPost<DbNote>('/api/noten', {
          schueler_id: schueler.id, klasse_id: schueler.klasse_id, fach: fach.trim(), art, note: wert, gewichtung: gewicht,
          datum, halbjahr, schuljahr, kommentar: kommentar.trim() || null,
        });
        toast('Note gespeichert', 'success');
        onGespeichert(n, true);
      } else {
        const r = await apiFetch<{ note: DbNote; endnote_festgelegt: number | null }>(`/api/noten/${note!.id}`, {
          method: 'PUT', body: JSON.stringify({ art, note: wert, gewichtung: gewicht, datum, kommentar: kommentar.trim() || null }),
        });
        toast('Note geändert', 'success');
        if (r.endnote_festgelegt != null)
          toast(`Für ${note!.halbjahr} ist bereits die Endnote ${r.endnote_festgelegt} festgelegt – sie bleibt unverändert.`, 'info', { dauerMs: 5000 });
        onGespeichert(r.note, false);
      }
    } catch (err: any) { setFehler(err?.message ?? 'Speichern fehlgeschlagen.'); }
    finally { setLaden(false); }
  }

  async function loeschen() {
    if (!note) return;
    onClose();
    const ja = await confirm(`Die Note ${note.note} (${ART_LABEL[note.art as Art]?.split(' ')[0] ?? note.art}, ${note.fach}) vom ${ttmm(note.datum)} wird gelöscht.`,
      { title: 'Note löschen?', confirmLabel: 'Löschen', danger: true });
    if (!ja) { onWiederOeffnen?.(); return; }
    try {
      await apiFetch(`/api/noten/${note.id}`, { method: 'DELETE' });
      toast('Note gelöscht', 'success');
      onGeloescht?.(note.id);
    } catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
  }

  return (
    <LdSheet open={open} onClose={onClose} maxWidth={480} ariaLabel={neu ? 'Note eintragen' : 'Note bearbeiten'}>
      <div style={{ padding: '8px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <LdSheetHeader
          icon={<MIcon name="edit" size={20} />}
          title={neu ? 'Note eintragen' : 'Note bearbeiten'}
          subtitle={`${schueler.name} · ${note ? `${note.halbjahr} ${schuljahrKurz(note.schuljahr)}` : `${halbjahr} ${schuljahrKurz(schuljahr)}`}`}
          onClose={onClose}
        />
        <LdField label="Fach" helper={neu ? undefined : 'Fach und Schüler lassen sich nicht ändern – dafür die Note löschen und neu anlegen.'}>
          {neu ? (
            <LdSelectField value={fach} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFach(e.target.value)} icon={<MIcon name="school" size={17} />}>
              {faecher.map(f => <option key={f} value={f}>{f}</option>)}
            </LdSelectField>
          ) : (
            <LdInputField value={fach} readOnly disabled icon={<MIcon name="lock" size={17} />} />
          )}
        </LdField>
        <LdField label="Art der Leistung">
          <LdChipGroup>
            {ARTEN.map(a => <LdChip key={a} active={art === a} onClick={() => setArt(a)}>{ART_LABEL[a].split(' ')[0]}</LdChip>)}
          </LdChipGroup>
        </LdField>
        <LdField label="Note">
          <LdGradePicker value={wert} onChange={setWert} farbeFn={notenFarbeKMK} />
        </LdField>
        <LdField label="Datum" helper={neu ? undefined : 'Nur innerhalb desselben Halbjahrs änderbar.'}>
          <LdDatum aria-label="Datum" required value={datum} onChange={e => setDatum(e.target.value)} />
        </LdField>
        {/* Welle N2: vor dem Speichern sagen, wie die Note zählt */}
        <LdField label="Gewichtung" helper={zaehltText(art, gewicht)}>
          <LdSegmented value={gewicht} onChange={setGewicht} options={[0.5, 1, 1.5, 2].map(g => ({ value: g, label: `×${g}` }))} />
        </LdField>
        <LdField label="Kommentar (optional)" error={fehler || undefined}>
          <LdTextarea value={kommentar} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setKommentar(e.target.value)} placeholder="z.B. Klausur sehr gut" rows={2} />
        </LdField>
        <LdSheetSaveBar onClick={speichern} disabled={!fach.trim()} loading={laden}>
          {neu ? `Note ${wert} speichern` : `Änderung speichern`}
        </LdSheetSaveBar>
        {!neu && (
          <button onClick={loeschen} className="ld-u6-loeschen">
            <MIcon name="delete" size={18} />Note löschen
          </button>
        )}
      </div>
    </LdSheet>
  );
}
